import { authenticatedFetch } from '../utils/authErrorHandler';

interface UploadResult {
  filePath: string;
}

interface SASResponseData {
  sasUrl: string;
  filePath: string;
}

interface ProgressInfo {
  loaded: number;
  total: number;
  percent: number | null;
}

type StatusChangeCallback = (status: string) => void;
type ProgressCallback = (progress: ProgressInfo) => void;

export async function uploadFileToProject({
  file,
  projectId,
  username,
  onStatusChange,
  onProgress
}: {
  file: File;
  projectId: string;
  username?: string;
  onStatusChange?: StatusChangeCallback;
  onProgress?: ProgressCallback;
}): Promise<UploadResult> {
  if (!file) {
    throw new Error('A file must be provided for upload');
  }

  if (!projectId) {
    throw new Error('No project available. Please wait for project to be initialized and try again.');
  }

  const fileServiceUrl: string | undefined = import.meta.env.VITE_FILE_SERVICE_BASE_URL;

  if (!fileServiceUrl) {
    throw new Error('VITE_FILE_SERVICE_BASE_URL environment variable is not configured');
  }

  const uploadUrl: string = `${fileServiceUrl}/sas/upload`;

  const sasRequest = {
    project_id: projectId,
    filename: file.name,
    content_type: file.type || 'application/octet-stream',
  };

  onStatusChange?.('fetching_sas');

  const sasResponse: Response = await authenticatedFetch(uploadUrl, {
    method: 'POST',
    body: JSON.stringify(sasRequest),
  });

  if (!sasResponse.ok) {
    let errorMessage: string = `Failed to get upload URL: ${sasResponse.status}`;
    try {
      const errorData = await sasResponse.json();
      if (errorData?.detail) {
        // Ensure detail is a string, not an object
        errorMessage = typeof errorData.detail === 'string'
          ? errorData.detail
          : JSON.stringify(errorData.detail);
      }
    } catch (err) {
      // Ignore JSON parse errors and fall back to default message
    }
    throw new Error(errorMessage);
  }

  const sasData: SASResponseData = await sasResponse.json();

  onStatusChange?.('uploading');

  await new Promise<void>((resolve, reject) => {
    const xhr: XMLHttpRequest = new XMLHttpRequest();
    // Backend returns camelCase 'sasUrl' (Phase 4: no fallbacks)
    xhr.open('PUT', sasData.sasUrl, true);
    xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');

    // Add blob metadata headers to pass username to Azure Storage
    // These will be included in the Event Grid event and picked up by file-process-service
    if (username) {
      xhr.setRequestHeader('x-ms-meta-created_by', username);
      xhr.setRequestHeader('x-ms-meta-last_updated_by', username);
    }

    xhr.upload.onprogress = (event: ProgressEvent): void => {
      if (!onProgress) return;
      if (event.lengthComputable) {
        const percent: number = Math.round((event.loaded / event.total) * 100);
        onProgress({ loaded: event.loaded, total: event.total, percent });
      } else {
        onProgress({ loaded: event.loaded, total: event.total, percent: null });
      }
    };

    xhr.onload = (): void => {
      if (xhr.status >= 200 && xhr.status < 300) {
        if (onProgress) {
          onProgress({ loaded: file.size, total: file.size, percent: 100 });
        }
        resolve();
      } else {
        reject(new Error(`Upload failed: ${xhr.status} ${xhr.statusText}`));
      }
    };

    xhr.onerror = (): void => {
      reject(new Error('Upload failed due to a network error.'));
    };

    xhr.send(file);
  });

  onStatusChange?.('uploaded');

  return {
    filePath: sasData.filePath,
  };
}
